import moment from 'moment';
import querystring from 'query-string';
import { DeepPartial } from 'utility-types';
import pkg from '../../package.json';
import { unlockNavigation } from '../preventNavigation';
import routeMap, { hashIndexed } from '../indexed-route-map';
import { FlowNode, FlowLike } from './types/flow';
import { Case } from './match';
import {
  DuplicateRoutesError,
  NonExistantRouteError,
  RoutesToSelfError,
  NoFlowsRegisteredError
} from './errors';
import { Action, RouteAction } from './actions';
import { InputMap } from './types/input-map';
import { StaticPrimaryCustomer } from './types/customer';
import { accountTypes, addCommas } from '../flows/shared/utils';

const validRoute = (routeSet, node: FlowNode, action: Action | undefined) => {
  if (!(action instanceof RouteAction)) return;
  //  verify no node points to an unregistered node.
  if (!routeSet.has(action.nextPath))
    throw new NonExistantRouteError(
      `A non-existant route was linked from "${
        node.route
      }" (action = ${JSON.stringify(action)}) Full: ${JSON.stringify(node)}`
    );

  //  verify no node points to itself
  if (action.nextPath === node.route) {
    throw new RoutesToSelfError(
      `Node "${node.route}" was trying to link to itself. Full ${JSON.stringify(
        node
      )}`
    );
  }
};

export const validateFlow = (flow: FlowLike) => {
  const { nodes } = flow;

  if (!nodes.length) {
    throw new NoFlowsRegisteredError(
      'You must add at least one flow to register!!!'
    );
  }

  /**
   * validate routes!
   * @todo move this to a build/compile time check from a runtime check.
   */

  const routes: string[] = nodes.map(f => f.route);
  const routeSet = new Set(routes);

  if (!routeSet.has(flow.start)) {
    throw new NonExistantRouteError(`start route: ${flow.start} doesn't exist`);
  }

  // verify no duplicate routes
  if (routeSet.size !== routes.length) {
    throw new DuplicateRoutesError(
      `Duplicate route(s) found during registration!!! ${routes.filter(
        (item, index) => routes.indexOf(item) !== index
      )}`
    );
  }

  nodes.forEach((node: FlowNode) => {
    const validRoutes = (cases?: Case<Action>[]) => {
      if (cases) cases.forEach(([, a]) => validRoute(routeSet, node, a));
    };

    validRoutes(node.preLoad);
    validRoutes(node.preCommit);
    validRoute(routeSet, node, node.next.default);
    validRoutes(node.next.cases);
    validRoute(routeSet, node, node.back?.default);
    validRoutes(node.back?.cases);
  });
};
/**
 * Given a set of numbers
 * a total distribute remainder
 * using Hare Largest Remainder Method
 * source: https://bitly.com/ (wiki)
 */

export const distributeRemainder = (
  amounts: number[],
  total: number
): number[] => {
  if (!amounts.length) return [];

  const amountObjs = amounts.map((a, idx) => ({
    idx,
    int: Math.floor(a),
    dec: a - Math.floor(a)
  }));
  const integerSum = amountObjs.reduce((acc, { int }) => acc + int, 0);
  const remainder = total - integerSum;
  const sortedAmounts = [...amountObjs].sort((a, b) => b.dec - a.dec);
  for (let i = 0; i < remainder; i++) {
    sortedAmounts[i % sortedAmounts.length].int += 1;
  }
  const getNewAmount = idx => sortedAmounts.find(a => a.idx === idx)?.int || 0;
  return amountObjs.map(({ idx }) => getNewAmount(idx));
};

export const getRemainder = (x, y) => {
  return parseFloat((parseFloat(x) - parseFloat(y)).toFixed(2));
};

// parses float and removes decimals if needed
export const parseAmount = value => {
  if (Math.round(value) === parseFloat(value)) {
    return parseFloat(parseFloat(value).toFixed());
  }
  // fixing to three decimal places to catch slight dollar updates to allocations
  // this prevents the user from moving forward when their allocation percent is 100.001%
  // DE35005 & DE35009
  return parseFloat(parseFloat(value).toFixed(3));
};

export const removeCommasAndParseFloat = value =>
  value ? parseFloat(value.toString().replace(/,/g, '')) : 0;

export const isObject = obj =>
  typeof obj === 'object' && obj !== null && !Array.isArray(obj);

// recursive merge of two objects...
// does not merge arrays
export const merge = (origObj, newObj) => {
  const output = { ...origObj };

  if (newObj === undefined) {
    return output;
  }

  Object.keys(newObj).forEach(prop => {
    if (isObject(origObj[prop]) && isObject(newObj[prop])) {
      output[prop] = merge(origObj[prop], newObj[prop]);
    } else {
      output[prop] = newObj[prop];
    }
  });
  return output;
};

// recursive merge of two objects...
// does not merge arrays
export function mergeTyped<A>(origObj: A, newObj: A): A {
  const output = { ...origObj };

  if (newObj === undefined || newObj === null) {
    return output;
  }

  Object.keys(newObj).forEach(prop => {
    if (isObject(origObj[prop]) && isObject(newObj[prop])) {
      output[prop] = mergeTyped<A>(origObj[prop], newObj[prop]);
    } else {
      output[prop] = newObj[prop];
    }
  });
  return output;
}

/* eslint-disable default-param-last */
export const sum = (list: any[] = [], key?): number =>
  list.reduce((total, cur) => total + parseFloat(key ? cur[key] : cur), 0);

export const transformFromYearFirstToMonthFirstFormat = (
  dateToTransform: string
) => {
  return moment(dateToTransform, 'YYYY-MM-DD').format('MM/DD/YYYY');
};

export const trasformFromMonthFirstToYearFirstFormat = (
  dateToTransform: string
) => {
  return moment(dateToTransform, 'MM/DD/YYYY').format('YYYY-MM-DD');
};

export const randomKey = (length = 10) =>
  Math.random()
    .toString(36)
    .slice(2, length + 2);

export const getRouteFromHash = (hash): string => {
  const route = hashIndexed[hash]?.fullRoutes?.[0] || '';
  return route;
};

export const getReadableStringFromHash = (hash): string => {
  const readableStep = hashIndexed[hash]?.hash?.readableStringID || '';
  return readableStep;
};

export const getCurrentRoute = (): string =>
  window.location.pathname
    .replace(/.*?nasu\/?/, '') // matches the first occurrence of nasu in the pathname
    .replace(/\/$/, '');

export const currentRouteToHash = (): string =>
  (routeMap || []).find(entry => entry?.fullRoutes?.includes(getCurrentRoute()))
    ?.hash?.short || '';

export const getLastVisitedRoute = (progressStepText: string) => {
  const lastHash = progressStepText.split(',').pop() || '';
  const res = hashIndexed[lastHash]?.fullRoutes?.[0];
  return res;
};

export const getSensitiveRoute = (progressStepText: string) => {
  const steps = progressStepText.split(',');
  return steps
    .map(hash => hashIndexed[hash])
    .find(node => node.startsPersistingSensitive)?.fullRoutes?.[0];
};

export const getBackRoute = (progressStepText): string => {
  const lastRoute = getLastVisitedRoute(progressStepText);
  const currentRoute = getCurrentRoute();

  // for rj we are navigated to the last entry in progressStepText
  // where generally we are one ahead
  // so this will go back to the second to last entry of progressStepText
  // if we are already on the last entry... weird but it makes sense kinda
  const currentRouteIsLastRoute: boolean = lastRoute === currentRoute;

  let route = lastRoute;

  if (currentRouteIsLastRoute)
    route = getLastVisitedRoute(
      progressStepText
        .split(',')
        .slice(0, -1)
        .join(',')
    );

  return route;
};

export const navigateToExternal = url => {
  unlockNavigation();
  window.location.href = url;
};

export const parseQueryString = str => str && querystring.parse(str);

export const parseCurrentQueryString = () =>
  parseQueryString(window.location.search);

// copied below from currNASU
// @derived from https://bit.ly/2OKVFy0
export const getQueryVariable = (
  queryString: string,
  variable: string
): string | boolean => {
  const query = queryString.substring(1);
  const vars = query.split('&');
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split('=');
    if (decodeURIComponent(pair[0]) === variable) {
      return decodeURIComponent(pair[1]);
    }
  }
  return false;
};

// **FIX** resolves nasu-svc profile validation errors, 400s
// split version to shorten lengthy snapshot versions
// i.e. nasu-ui@4.0.0-tester-DE11111-fix-broken-thing-11999 --> nasu-ui@4.0.0
// name.split removes substring before /; causes profile svc validation error
export const APP_VERSION = `${pkg.name.split('/')?.[1]}@${pkg.version.split(
  '-',
  1
)}`;

export const APP_NAME = 'NASU';

export const withDefault = (data: any, def: any): any => data || def;

export const isEmpty = (x: any): any =>
  x === undefined || x === null || (typeof x === 'string' && x.trim() === '');

const allObjectValuesEmpty = (object: any = {}): boolean =>
  Object.values(object).every(val => isEmpty(val));

export const isEmptyArray = (arrObj: any[] = []) => {
  if (arrObj.length !== 0) {
    const arrayElementResults: boolean[] = arrObj.map(arrayElement => {
      if (arrayElement?.constructor === Object) {
        return allObjectValuesEmpty(arrayElement);
      }

      return isEmpty(arrayElement);
    });
    return arrayElementResults.every(bool => bool);
  }

  return true;
};

export const isEmptyObject = (object: any = {}): boolean => {
  if (object?.constructor === Object) {
    const objectHasNoKeys = Object.keys(object).length === 0;
    return objectHasNoKeys || allObjectValuesEmpty(object);
  }
  // Check for array type and do some recursive checks for object elements
  if (Array.isArray(object)) {
    return isEmptyArray(object);
  }
  return isEmpty(object);
};

export const authenticatedWithDefault = (
  authData: any,
  unauthData: any,
  def: any
): any => authData || unauthData || def;

export const titleCaseForString = str => {
  if (typeof str === 'string') {
    return str.replace(
      /\w\S*/g,
      c => c.charAt(0).toUpperCase() + c.substr(1).toLowerCase()
    );
  }

  return '';
};

/**
 * Return the cookie value mapped to the name passed to the function
 * @type {function}
 * @param {string} cookieName
 * @return {string}
 */
export const getCookie = cookieName => {
  const regex = new RegExp(`(?:;\\s)*${cookieName}=([^;]*)`);
  const match = document.cookie.match(regex);
  if (match) {
    const cookieValue = match[1];
    return cookieValue;
  }
  return '';
};

/**
 * Creates a new cookie and returns true after the cookie is set
 * @param {string} cookieName
 * @param {*} value
 * @param {string} expiration
 * @param {boolean} secure
 * @return {boolean}
 */
export const setCookie = (
  cookieName,
  value,
  expiration = '',
  secure = false
) => {
  const isSecure = secure && window.location.protocol === 'https:';
  document.cookie = `${cookieName}=${value}; path=/; expires=${expiration}${
    isSecure ? ' ;secure' : ''
  }`;
  return true;
};

export const setStageValCookie = () => {
  document.cookie = `stagemf=foo; path=/; domain=troweprice.com`;
  return true;
};

/**
 * Deletes the given cookie and returns true
 * @param {string} cookieName
 * @return {boolean}
 */
export const deleteCookie = cookieName => {
  document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
  return true;
};

/**
 * Deletes existing bpId and uberId cookies
 * @return {void}
 */
export const deleteExistingBpIdUberIdCookies = () => {
  deleteCookie('bpId');
  deleteCookie('uberId');
};

/**
 * Deletes existing delegateReset cookies
 * @return {void}
 */
export const deleteExistingDelegateResetCookies = () =>
  deleteCookie('delegateReset');

export const deleteStageValCookie = () => {
  document.cookie = `stagemf=; path=/; domain=troweprice.com; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
  return true;
};

export const dollarMask = value => value && addCommas(value);

export const withLock = function<T>(
  fn: (...args: any[T]) => T
): (...args: any[]) => T | undefined {
  let lock = false;
  const unlock = (): void => {
    lock = false;
  };
  return (...args): T | undefined => {
    if (lock) return undefined;

    lock = true;
    return fn(unlock, ...args);
  };
};

export const getUserInfo = ({
  accountType,
  trustee,
  customer
}: {
  accountType: accountTypes;
  trustee: StaticPrimaryCustomer;
  customer: StaticPrimaryCustomer;
}): DeepPartial<StaticPrimaryCustomer> =>
  accountType === accountTypes.TRUST ? trustee : customer;

export const isNonEmptyString = value =>
  typeof value === 'string' && value.length;

// cleanup whitespace between joined strings (i.e. full names, street addresses, etc.)
// first, locates all instances of spaces between words of the string
// then replaces each space with a single space (cleans double spaces when joined string is empty, i.e. '')
// wraps up with trimming extra spaces at the beginning and end of the string
export const trimJoinedStringWhitespace = (joinedStr: string): string =>
  joinedStr.replace(/\s+/g, ' ').trim();

export const trimInputObj = (obj): InputMap => {
  return Object.keys(obj).reduce(
    (acc, cur) => ({
      ...acc,
      [cur]: isNonEmptyString(obj[cur]) ? obj[cur].trim() : obj[cur]
    }),
    {}
  );
};

export const hasTruthyValues = obj => Object.values(obj || {}).some(Boolean);
